import type { Language } from '@seek/melways-sites';

import {
  type ApplicationFilters,
  ApplicationSort,
  ApplicationStatusFolder,
  LocationDisplayFormat,
  QuestionnaireFilterOperator,
  SortOrder,
} from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import { fetchAllApplications } from 'src/graphql/fetchAllApplications.hirer.graphql';
import { fetchApplications } from 'src/graphql/fetchApplications.hirer.graphql';
import { navigateToSignInPageWithReturnUrl } from 'src/pages/CandidateManagementPage/utils/utils';
import { isValidId } from 'src/utils/advertiserIds';
import { logError } from 'src/utils/logError';

import {
  BUCKETS,
  type SearchFilters,
} from '../../stores/JobApplicationStore/types';
import adCenterApiService from '../adCenterApiService';
import configurationService from '../configurationService';

import { DEFAULT_SORT_OPTION } from './../../constants/sortOrder';

const canFetchApplicationsFromHirerGraph = (jobId: number): boolean =>
  isValidId(jobId.toString());

const mapToSort = (sortBy: string) => {
  const sortMap: Record<
    string,
    { sortField: ApplicationSort; orderBy: SortOrder }
  > = {
    'matching-answers': {
      sortField: ApplicationSort.SCORE,
      orderBy: SortOrder.DESC,
    },
    'non-matching-answers': {
      sortField: ApplicationSort.SCORE,
      orderBy: SortOrder.ASC,
    },
    'a-z-name': {
      sortField: ApplicationSort.NAME,
      orderBy: SortOrder.ASC,
    },
    'z-a-name': {
      sortField: ApplicationSort.NAME,
      orderBy: SortOrder.DESC,
    },
    'newest-application': {
      sortField: ApplicationSort.APPLICATION_DATE,
      orderBy: SortOrder.DESC,
    },
    'oldest-application': {
      sortField: ApplicationSort.APPLICATION_DATE,
      orderBy: SortOrder.ASC,
    },
    'highest-rating': {
      sortField: ApplicationSort.RATING,
      orderBy: SortOrder.DESC,
    },
    'lowest-rating': {
      sortField: ApplicationSort.RATING,
      orderBy: SortOrder.ASC,
    },
    relevance: {
      sortField: ApplicationSort.RELEVANCE,
      orderBy: SortOrder.DESC,
    },
    'search-relevance': {
      sortField: ApplicationSort.SEARCH_RELEVANCE,
      orderBy: SortOrder.DESC,
    },
  };

  const sort = sortMap[sortBy];

  if (!sort) {
    logError(
      {
        errorCode: 'UNKNOWN_SORT_TYPE',
        description: 'Sort option unable to be found - Unknown sort option',
      },
      {
        requestedSortOption: sortBy,
      },
    );
    return sortMap['matching-answers'];
  }

  return sort;
};

const mapToStatusFolders = (
  bucket: BUCKETS,
  searchText: string,
): ApplicationStatusFolder[] | undefined => {
  if (searchText !== '') {
    return undefined;
  }

  switch (bucket) {
    case BUCKETS.INBOX:
      return [ApplicationStatusFolder.INBOX];
    case BUCKETS.PRESCREEN:
      return [ApplicationStatusFolder.PRESCREEN];
    case BUCKETS.SHORTLIST:
      return [ApplicationStatusFolder.SHORTLIST];
    case BUCKETS.INTERVIEW:
      return [ApplicationStatusFolder.INTERVIEW];
    case BUCKETS.OFFER:
      return [ApplicationStatusFolder.OFFER];
    case BUCKETS.ACCEPT:
      return [ApplicationStatusFolder.ACCEPT];
    case BUCKETS.NOT_SUITABLE:
      return [ApplicationStatusFolder.NOT_SUITABLE];
    default:
      return undefined;
  }
};

const mapToQuestionnaireFilters = (filters: SearchFilters) =>
  filters.rrGlobalFilters.map((filter) => ({
    ...filter,
    operator:
      filter.operator === 'AND'
        ? QuestionnaireFilterOperator.AND
        : QuestionnaireFilterOperator.OR,
  }));

const mapToApplicationFilters = ({
  searchText,
  bucket,
  filters,
}: {
  searchText: string;
  bucket: BUCKETS;
  filters: SearchFilters;
}): ApplicationFilters => ({
  searchText,
  homeLocationIds: filters.homeLocationIds,
  questionnaireFilters: mapToQuestionnaireFilters(filters),
  statusFolders: mapToStatusFolders(bucket, searchText),
  nationalityRightToWorkIds: filters.nationalityRightToWorkIds,
});

class JobApplicationService {
  async fetch(
    jobId: number,
    searchText: string,
    language: Language,
    displayCountry: string,
    bucket: BUCKETS = BUCKETS.INBOX,
    sortBy = DEFAULT_SORT_OPTION.value,
    pageNumber: number = 1,
    filters: SearchFilters,
  ): Promise<Awaited<ReturnType<typeof fetchApplications>> | 'redirect'> {
    if (!canFetchApplicationsFromHirerGraph(jobId)) {
      navigateToSignInPageWithReturnUrl();
      return 'redirect';
    }

    return await fetchApplications({
      input: {
        jobId,
        pagination: {
          pageNumber,
        },
        sort: mapToSort(sortBy),
        filters: mapToApplicationFilters({
          searchText,
          bucket,
          filters,
        }),
      },
      nationalitiesInput2: {
        jobId,
      },
      displayLabelInput2: {
        language,
      },
      countryNameInput2: {
        language,
      },
      displayDescriptionInput2: {
        language,
        displayFormat: LocationDisplayFormat.SHORT,
        displayCountry,
      },
    });
  }

  async exportApplicationsToCsv(positionId: number) {
    const body = {
      positionID: positionId,
    };

    return adCenterApiService.post(
      configurationService.getRelativeUrl('/api/v2/prospectData/getCsvAll'),
      body,
      (json) => json,
    );
  }

  async fetchAllJobApplications(
    searchText: string,
    bucket: BUCKETS = BUCKETS.INBOX,
    sortBy = DEFAULT_SORT_OPTION.value,
    filters: SearchFilters,
    jobId: number,
  ): Promise<Awaited<ReturnType<typeof fetchAllApplications>> | 'redirect'> {
    if (!canFetchApplicationsFromHirerGraph(jobId)) {
      navigateToSignInPageWithReturnUrl();
      return 'redirect';
    }

    return await fetchAllApplications({
      jobId,
      sort: mapToSort(sortBy),
      filters: mapToApplicationFilters({
        searchText,
        bucket,
        filters,
      }),
    });
  }
}

export default new JobApplicationService();
